import { useMutation } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import {
  Button,
  Checkbox,
  Dialog,
  IconButton,
  Portal,
  Text,
} from 'react-native-paper';
import useConfirmModal from 'shared/hooks/useConfirmModal';
import useOrientation from 'shared/hooks/useOrientation';
import useSnackBar from 'shared/hooks/useSnackBar';
import { colors, sizes } from 'shared/styles';

interface AppSubscription {
  statusMessage: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
}

interface User {
  id: string;
  name: string;
  email?: string;
  hasEmail: boolean;
  c2Username?: string;
  appSubscription?: AppSubscription;
}

interface Props {
  users: User[];
  onDismiss: () => void;
  onSuccess: () => void;
}

const MOVE_USER_MUTATION = gql`
  mutation MoveUser($input: MoveUserInput!) {
    userMove(input: $input) {
      id
      name
    }
  }
`;

const MergeUsersModal = ({ users, onDismiss, onSuccess }: Props) => {
  const confirmWith = useConfirmModal();
  const showSnackBar = useSnackBar();
  const { vh } = useOrientation();
  const [orderedUsers, setOrderedUsers] = useState(users);
  const [copy, setCopy] = useState<'email' | 'c2' | null>(null);

  const [moveUser, { loading }] = useMutation(MOVE_USER_MUTATION);

  // Determine which user is "from" and which is "to" based on orderedUsers
  const actualFromUser = orderedUsers[0];
  const actualToUser = orderedUsers[1];

  // Only show copy email checkbox if source has email and destination doesn't
  const canCopyEmail = actualFromUser.hasEmail && !actualToUser.hasEmail;
  // Only show copy C2 checkbox if source has C2 and destination doesn't
  const canCopyC2 = !!actualFromUser.c2Username && !actualToUser.c2Username;

  const handleSwap = () => {
    setOrderedUsers([orderedUsers[1], orderedUsers[0]]);
    // Reset copy selection when swapping
    setCopy(null);
  };

  const displayError = (message: string) => {
    setTimeout(() => {
      confirmWith({ title: 'Error', message, hideCancel: true });
    }, 500);
  };

  const onMove = () => {
    const fromUserDisplay = `${actualFromUser.name} (${actualFromUser.email || '-'}${
      actualFromUser.c2Username && !actualFromUser.hasEmail ? ' via C2' : ''
    })`;
    const toUserDisplay = `${actualToUser.name} (${actualToUser.email || '-'}${
      actualToUser.c2Username && !actualToUser.hasEmail ? ' via C2' : ''
    })`;

    confirmWith({
      message: `Are you sure you want to merge all data from:\n\n"${fromUserDisplay}"\n\nto:\n\n"${toUserDisplay}"?\n\nThis will delete the source user account.`,
      confirmLabel: 'Merge Users',
      confirmColor: colors.destructiveColor,
      onConfirm: () => {
        moveUser({
          variables: {
            input: {
              fromId: actualFromUser.id,
              toId: actualToUser.id,
              copy: copy || undefined,
            },
          },
        })
          .then(() => {
            onSuccess();
            showSnackBar({
              message: `Successfully merged users "${actualFromUser.name}" and "${actualToUser.name}"`,
            });
          })
          .catch(({ graphQLErrors }) => {
            const message =
              graphQLErrors &&
              graphQLErrors.map((e: any) => e.message).join(', ');
            displayError(message || 'Error moving user. Please, try again!');
          });
      },
    });
  };

  return (
    <Portal>
      <Dialog
        visible
        onDismiss={onDismiss}
        style={[{ maxHeight: vh(60) }, styles.dialog]}
      >
        <Dialog.Title>Merge Users</Dialog.Title>
        <View
          style={{
            marginHorizontal: 20,
            marginBottom: sizes.spacing,
          }}
        >
          <View
            style={{
              marginBottom: 15,
              padding: 10,
              backgroundColor: '#f5f5f5',
              borderRadius: 4,
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              <Text style={{ fontWeight: 'bold' }}>From User:</Text>
              <IconButton
                icon="open-in-new"
                size={20}
                onPress={() => {
                  window.open(`/users/${actualFromUser.id}`, '_blank');
                }}
                style={{ margin: 0 }}
              />
            </View>
            <Text style={{ marginBottom: 4 }}>
              Name:{' '}
              <Text style={{ fontWeight: 'bold' }}>{actualFromUser.name}</Text>
            </Text>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                marginBottom: 4,
              }}
            >
              <Text>Email: {actualFromUser.email || '-'}</Text>
              {actualFromUser.c2Username && !actualFromUser.hasEmail && (
                <Text
                  style={{
                    marginLeft: 8,
                    fontSize: 12,
                    color: '#666',
                    fontStyle: 'italic',
                  }}
                >
                  (via C2)
                </Text>
              )}
            </View>
            <Text style={{ marginBottom: 4 }}>
              C2 Username: {actualFromUser.c2Username || '-'}
            </Text>
            <Text>
              Subscription Status:{' '}
              <Text style={{ fontWeight: 'bold' }}>
                {actualFromUser.appSubscription?.statusMessage || '-'}
              </Text>
            </Text>
          </View>

          <Button
            icon="swap-vertical"
            onPress={handleSwap}
            style={{ marginBottom: 15 }}
          >
            Swap Users
          </Button>

          <View
            style={{
              marginBottom: 15,
              padding: 10,
              backgroundColor: '#f5f5f5',
              borderRadius: 4,
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              <Text style={{ fontWeight: 'bold' }}>To User:</Text>
              <IconButton
                icon="open-in-new"
                size={20}
                onPress={() => {
                  window.open(`/users/${actualToUser.id}`, '_blank');
                }}
                style={{ margin: 0 }}
              />
            </View>
            <Text style={{ marginBottom: 4 }}>
              Name:{' '}
              <Text style={{ fontWeight: 'bold' }}>{actualToUser.name}</Text>
            </Text>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                marginBottom: 4,
              }}
            >
              <Text>Email: {actualToUser.email || '-'}</Text>
              {actualToUser.c2Username && !actualToUser.hasEmail && (
                <Text
                  style={{
                    marginLeft: 8,
                    fontSize: 12,
                    color: '#666',
                    fontStyle: 'italic',
                  }}
                >
                  (via C2)
                </Text>
              )}
            </View>
            <Text style={{ marginBottom: 4 }}>
              C2 Username: {actualToUser.c2Username || '-'}
            </Text>
            <Text>
              Subscription Status:{' '}
              <Text style={{ fontWeight: 'bold' }}>
                {actualToUser.appSubscription?.statusMessage || '-'}
              </Text>
            </Text>
          </View>

          {(canCopyEmail || canCopyC2) && (
            <View style={{ marginBottom: 10 }}>
              {canCopyEmail && (
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    marginBottom: 10,
                  }}
                >
                  <Checkbox.Android
                    status={copy === 'email' ? 'checked' : 'unchecked'}
                    onPress={() => setCopy(copy === 'email' ? null : 'email')}
                  />
                  <Text style={{ marginLeft: 10 }}>Copy email address</Text>
                </View>
              )}
              {canCopyC2 && (
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Checkbox.Android
                    status={copy === 'c2' ? 'checked' : 'unchecked'}
                    onPress={() => setCopy(copy === 'c2' ? null : 'c2')}
                  />
                  <Text style={{ marginLeft: 10 }}>Copy Concept2 account</Text>
                </View>
              )}
            </View>
          )}
        </View>

        <Dialog.Actions style={styles.actionsContainer}>
          <Button mode="outlined" onPress={onDismiss}>
            Cancel
          </Button>
          <Button
            loading={loading}
            disabled={loading}
            mode="contained"
            icon="account-switch"
            onPress={onMove}
            style={{ backgroundColor: colors.destructiveColor }}
          >
            Merge Users
          </Button>
        </Dialog.Actions>
      </Dialog>
    </Portal>
  );
};

const styles = StyleSheet.create({
  dialog: {
    alignSelf: 'center',
    width: 500,
  },
  actionsContainer: {
    paddingVertical: sizes.spacing,
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
});

export default MergeUsersModal;
